"use server"

import { sdk } from "@lib/config"
import { sortProducts } from "@lib/util/sort-products"
import { HttpTypes } from "@medusajs/types"
import { SortOptions } from "@modules/store/components/refinement-list/sort-products"
import { getAuthHeaders, getCacheOptions } from "./cookies"
import { getRegion, retrieveRegion } from "./regions"

export const listProducts = async ({
  pageParam = 1,
  queryParams,
  countryCode,
  regionId,
  withAuth = false,
  cacheTags = true,
  revalidate,
}: {
  pageParam?: number
  queryParams?: HttpTypes.FindParams & HttpTypes.StoreProductListParams
  countryCode?: string
  regionId?: string
  withAuth?: boolean
  cacheTags?: boolean
  revalidate?: number
}): Promise<{
  response: { products: HttpTypes.StoreProduct[]; count: number }
  nextPage: number | null
  queryParams?: HttpTypes.FindParams & HttpTypes.StoreProductListParams
}> => {
  if (!countryCode && !regionId) {
    throw new Error("Country code or region ID is required")
  }

  const limit = queryParams?.limit || 12
  const _pageParam = Math.max(pageParam, 1)
  const offset = _pageParam === 1 ? 0 : (_pageParam - 1) * limit

  let region: HttpTypes.StoreRegion | undefined | null

  if (countryCode) {
    region = await getRegion(countryCode)
  } else {
    region = await retrieveRegion(regionId!)
  }

  if (!region) {
    return {
      response: { products: [], count: 0 },
      nextPage: null,
    }
  }

  const headers = withAuth ? { ...(await getAuthHeaders()) } : {}

  const next = {
    ...(cacheTags ? await getCacheOptions("products") : {}),
    revalidate: revalidate ?? 3600,
  }

  return sdk.client
    .fetch<{ products: HttpTypes.StoreProduct[]; count: number }>(
      `/store/products`,
      {
        method: "GET",
        query: {
          limit,
          offset,
          region_id: region?.id,
          fields:
            "*variants.calculated_price,+variants.inventory_quantity,*variants.images,+metadata,+tags,*categories,*images",
          ...queryParams,
        },
        headers,
        next,
        cache: "force-cache",
      }
    )
    .then(({ products, count }) => {
      const nextPage = count > offset + limit ? pageParam + 1 : null

      return {
        response: {
          products,
          count,
        },
        nextPage: nextPage,
        queryParams,
      }
    })
}

/**
 * This will fetch 100 products to the Next.js cache and sort them based on the sortBy parameter.
 * It will then return the paginated products based on the page and limit parameters.
 */
export const listProductsWithSort = async ({
  page = 0,
  queryParams,
  sortBy = "created_at",
  countryCode,
  variantFilters,
}: {
  page?: number
  queryParams?: HttpTypes.FindParams & HttpTypes.StoreProductParams
  sortBy?: SortOptions
  countryCode: string
  variantFilters?: Record<string, string[]>
}): Promise<{
  response: { products: HttpTypes.StoreProduct[]; count: number }
  nextPage: number | null
  queryParams?: HttpTypes.FindParams & HttpTypes.StoreProductParams
}> => {
  const limit = queryParams?.limit || 12
  const hasVariantFilters =
    !!variantFilters &&
    Object.values(variantFilters).some((values) => Array.isArray(values) && values.length > 0)

  if (!hasVariantFilters && sortBy === "created_at") {
    const { response } = await listProducts({
      pageParam: page,
      queryParams: {
        ...queryParams,
        limit,
        order: "created_at",
        fields:
          "id,handle,title,thumbnail,images,*variants.images,*variants.calculated_price,*variants.options,options.*",
      },
      countryCode,
    })

    const nextPage = response.count > page * limit ? page + 1 : null

    return {
      response,
      nextPage,
      queryParams,
    }
  }

  const {
    response: { products, count },
  } = await listProducts({
    pageParam: 0,
    queryParams: {
      ...queryParams,
      limit: 100,
      fields:
        "id,handle,title,thumbnail,images,*variants.images,*variants.calculated_price,*variants.options,options.*",
    },
    countryCode,
  })

  // Apply variant filters
  let filteredProducts = products
  if (variantFilters && Object.keys(variantFilters).length > 0) {
    filteredProducts = products.filter((product) => {
      // Check if product has any variant matching all the filter criteria
      return product.variants?.some((variant) => {
        return Object.entries(variantFilters).every(([optionTitle, values]) => {
          if (!values || values.length === 0) return true
          // Find the option value for this variant
          const optionValue = variant.options?.find(
            (opt) => opt.option?.title?.toLowerCase() === optionTitle.toLowerCase()
          )?.value
          return optionValue && values.includes(optionValue)
        })
      })
    })
  }

  const sortedProducts = sortProducts(filteredProducts, sortBy)

  const pageParam = (page - 1) * limit
  const filteredCount = filteredProducts.length

  const nextPage = filteredCount > pageParam + limit ? pageParam + limit : null

  const paginatedProducts = sortedProducts.slice(pageParam, pageParam + limit)

  return {
    response: {
      products: paginatedProducts,
      count: filteredCount,
    },
    nextPage,
    queryParams,
  }
}

/**
 * Search products by query string
 */
export const searchProducts = async ({
  query,
  countryCode,
  limit = 6,
}: {
  query: string
  countryCode: string
  limit?: number
}): Promise<HttpTypes.StoreProduct[]> => {
  if (!query || query.length < 2) {
    return []
  }

  const region = await getRegion(countryCode)

  if (!region) {
    return []
  }

  const headers = {
    ...(await getAuthHeaders()),
  }

  const next = {
    ...(await getCacheOptions("products")),
  }

  try {
    const { products } = await sdk.client.fetch<{ products: HttpTypes.StoreProduct[] }>(
      `/store/products`,
      {
        method: "GET",
        query: {
          q: query,
          limit,
          region_id: region.id,
          fields: "*variants.calculated_price,+variants.inventory_quantity,*images",
        },
        headers,
        next,
        cache: "force-cache",
      }
    )

    return products || []
  } catch (error) {
    console.error("Search error:", error)
    return []
  }
}

/**
 * Get all available product options (like Size, Color) from products
 */
export const getProductOptions = async ({
  countryCode,
  categoryId,
  collectionId,
}: {
  countryCode: string
  categoryId?: string
  collectionId?: string
}): Promise<{ id: string; title: string; values: string[] }[]> => {
  const queryParams: any = { limit: 100 }

  if (categoryId) {
    queryParams.category_id = [categoryId]
  }

  if (collectionId) {
    queryParams.collection_id = collectionId
  }

  const {
    response: { products },
  } = await listProducts({
    pageParam: 0,
    queryParams: {
      ...queryParams,
      fields: "options.*",
    },
    countryCode,
  })

  // Extract unique options from all products
  const optionsMap = new Map<string, Set<string>>()

  products.forEach((product) => {
    product.options?.forEach((option) => {
      if (option.title) {
        if (!optionsMap.has(option.title)) {
          optionsMap.set(option.title, new Set())
        }
        option.values?.forEach((val) => {
          if (val.value) {
            optionsMap.get(option.title)!.add(val.value)
          }
        })
      }
    })
  })

  // Convert to array format
  return Array.from(optionsMap.entries()).map(([title, valuesSet], index) => ({
    id: `option-${index}`,
    title,
    values: Array.from(valuesSet).sort(),
  }))
}
